import React, { useEffect, useState } from "react";
import AppMenu from "../components/AppMenu";
import { Table } from "reactstrap";
import { getStatesById, getStateData, getStateLogsById } from "../services/stateAPI";
import moment from "moment";
import { useParams, useNavigate } from "react-router";
import { title } from "../config";
import { sitePhpVersions, siteWordPressVersions } from "../services/constant";
import { BackButton } from "../components/BackButton";
import { useToast } from "../components/ToastContext";
import Pagination from "../components/Pagination";
import '../assets/css/State.css';

export function State() {
  const { showToast } = useToast();
  const [items, setItems] = useState([]);
  const [logs, setLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [logsPerPage, setLogsPerPage] = useState(100);
  const StateLogsTable = ({ logs, currentPage, logsPerPage }) => (
    <Table className="table table-hover" responsive hover>
      <thead>
        <tr>
          <th>Sr. No.</th>
          <th>Link</th>
          <th>Checked At</th>
          <th>Running Status</th>
          <th>Error</th>
        </tr>
      </thead>
      <tbody>
        {logs.map((log, index) => (
          <tr key={log._id || index}>
            <td>{(currentPage - 1) * logsPerPage + (index + 1)}</td>
            <td className="link-cell">
              {log.link ? (
                <a href={log.link} target="_blank" rel="noopener noreferrer" title={log.link}>
                  {new URL(log.link).hostname}
                </a>
              ) : (
                "-"
              )}
            </td>
            <td>{moment(log.checkedAt).format("DD MMM YYYY, hh:mm A")}</td>
            <td>
              {log.runningStatus ? (
                <span className="text-success">SUCCESS</span>
              ) : (
                <span className="text-danger">ERROR</span>
              )}
            </td>
            <td className={log.error ? "text-danger" : "text-success"}>
              {log.error ? (
                <span title={log.error}>{log.error}</span>
              ) : (
                "No Error"
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    reloadSite(id);
    fetchLogs(id);

    const interval = setInterval(() => {
      reloadSite(id);
      fetchLogs(id);
    }, 15 * 60 * 1000);

    return () => clearInterval(interval);
  }, [id]);

  const reloadSite = async (id) => {
    if (id !== undefined) {
      setItems(await getStatesById(id, showToast));
    } else {
      setItems(await getStateData(showToast));
    }
  };

  const fetchLogs = async (siteId, page = 1, perPage = logsPerPage) => {
    if (!siteId) return;
    const result = await getStateLogsById(siteId, page, perPage, showToast);

    setLogs(result.logs);
    setCurrentPage(page);
    setTotalPages(Math.ceil(result.totalLogs / perPage));
  };

  return (
    <div className="state-page px-2">
      <div className="px-2 sticky-header">
        <h3>{title} | States</h3>
        <AppMenu title="States" />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '5px 0' }}>
        <BackButton icon="←" />
        <h5 style={{ margin: 0 }}>Current Status</h5>
      </div>
      <StateList items={items} />

      {logs.length > 0 && (
        <>
          <h5 className="mt-4">Running Status Log History</h5>
          <div className="state-logs-wrapper" style={{ paddingBottom: "calc(40px + 2rem)" }}>
            <StateLogsTable
              logs={logs}
              currentPage={currentPage}
              logsPerPage={logsPerPage}
            />

          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={logsPerPage}
            onPageChange={(page) => fetchLogs(id, page, logsPerPage)}
            onPageSizeChange={(size) => {
              setLogsPerPage(size);
              fetchLogs(id, 1, size);
            }}
          />
        </>
      )}

    </div>
  );
}

// Helper functions
// const outLogURL = (domain) => `https://core.${domain}/out-log`;
// const errorLogURL = (domain) => `https://core.${domain}/error-log`;
// const getFreeSpace = (free, size) => {
//   const percentageFull = ((size - free) / size) * 100;
//   return Math.round(percentageFull);
// };

// Shared Table Component
const StateList = ({ items }) => {
  const [localItems, setLocalItems] = useState(Array.isArray(items) ? [...items] : []);
  const [toggleSorting, setToggleSorting] = useState(0);

  useEffect(() => {
    setLocalItems(Array.isArray(items) ? items : []);
  }, [items]);

  const sortTable = (field) => {
    let sortedData = [];
    if (toggleSorting === 1) {
      sortedData = [...localItems].sort((a, b) => (a[field] > b[field] ? -1 : a[field] < b[field] ? 1 : 0));
      setToggleSorting(0);
    } else {
      sortedData = [...localItems].sort((a, b) => (a[field] < b[field] ? -1 : a[field] > b[field] ? 1 : 0));
      setToggleSorting(1);
    }
    setLocalItems(sortedData);
  };

  return (
    <Table className="table table-hover" responsive hover>
      <thead>
        <tr>
          <th onClick={() => sortTable("name")} className="cursor-pointer">Name</th>
          <th onClick={() => sortTable("link")} className="cursor-pointer">Link</th>
          <th onClick={() => sortTable("runningStatus")} className="cursor-pointer">Running Status</th>
          <th onClick={() => sortTable("status")} className="cursor-pointer">Site Status</th>
          <th onClick={() => sortTable("sslExpiry")} className="cursor-pointer">SSL Expiry</th>
          <th onClick={() => sortTable("domainExpiry")} className="cursor-pointer">Domain Expiry</th>
          <th onClick={() => sortTable("phpVersion")} className="cursor-pointer">PHP Version</th>
          <th onClick={() => sortTable("wordPressVersion")} className="cursor-pointer">WordPress Version</th>
          <th onClick={() => sortTable("lastUpdated")} className="cursor-pointer">Last Updated</th>
          <th onClick={() => sortTable("lastError")} className="cursor-pointer">Error Log</th>
        </tr>
      </thead>
      <tbody>
        {localItems.map((item, index) => (
          <tr key={item._id || index}>
            <td>{item.name || '-'}</td>
            <td className="link-cell">
              {item.link ? (
                <a href={item.link} target="_blank" rel="noopener noreferrer" title={item.link}>
                  {new URL(item.link).hostname}
                </a>
              ) : (
                "-"
              )}
            </td>
            <td>
              {item.runningStatus === true ? (
                <span className="text-success">SUCCESS</span>
              ) : item.runningStatus === false ? (
                <span className="text-danger">ERROR</span>
              ) : (
                '-'
              )}
            </td>
            <td>
              {item.status === true ? (
                <span className="text-success">Active</span>
              ) : item.status === false ? (
                <span className="text-danger">Inactive</span>
              ) : (
                '-'
              )}
            </td>
            <td>
              {item.sslExpiry && item.sslExpiry !== 'NO_CERT_INFO'
                ? moment(item.sslExpiry).format("DD/MM/YYYY")
                : "-"}
            </td>
            <td>
              {item.domainExpiry
                ? moment(item.domainExpiry).format('DD/MM/YYYY')
                : '-'}
            </td>
            <td>
              {sitePhpVersions.find(v => v.value === String(item.phpVersion))?.name || '-'}
            </td>
            <td>
              {siteWordPressVersions.find(v => v.value === String(item.wordPressVersion))?.name || '-'}
            </td>
            <td>
              {item.lastUpdated
                ? moment(item.lastUpdated).format("DD MMM YYYY, hh:mm A")
                : "-"}
            </td>
            <td className={item.lastError ? "text-danger" : "text-success"}>
              {item.lastError ? (
                <span title={item.lastError}>{item.lastError}</span>
              ) : "No Error"}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};
